import type { ReactNode } from "react";
import type { NomenclatureNumbers } from "./nomenclatureNumbers";

interface Props {
  numbers: NomenclatureNumbers | null;
  pendingCodeCount: number;
  onShowPendingCodes: () => void;
}

const TILE_CLASSES = "flex min-w-0 flex-col gap-1 rounded-lg border px-3.5 py-3 text-left";
const LABEL_CLASSES = "text-[11.5px] font-semibold uppercase tracking-[0.06em] text-[#a9b0a1]";
const SUB_CLASSES = "text-xs text-[#a9b0a1]";

function StatTile({ label, value, sub }: { label: string; value: ReactNode; sub: string }) {
  return (
    <div className={`${TILE_CLASSES} border-rs-jet bg-rs-off-black`}>
      <span className={LABEL_CLASSES}>{label}</span>
      <span className="text-[22px] font-semibold leading-tight text-white tabular-nums">{value}</span>
      <span className={SUB_CLASSES}>{sub}</span>
    </div>
  );
}

export function NomenclatureStats({ numbers, pendingCodeCount, onShowPendingCodes }: Props) {
  const loadingValue = "…";
  const hasPending = pendingCodeCount > 0;
  const gradesWithTdsValue = numbers
    ? numbers.gradeCount
      ? `${numbers.gradesWithTdsCount} / ${numbers.gradeCount}`
      : "0"
    : loadingValue;
  const gradesWithTdsSub = numbers
    ? numbers.gradeCount
      ? `${numbers.gradeCount - numbers.gradesWithTdsCount} still missing one`
      : "save grades first"
    : "";

  return (
    <div className="grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(160px,1fr))]" aria-label="Nomenclature at a glance">
      <StatTile label="Grades" value={numbers?.gradeCount ?? loadingValue} sub="attached to an RS code" />
      <StatTile label="RS codes" value={numbers?.rsCodeCount ?? loadingValue} sub="stored once each" />
      <StatTile label="Approved codes" value={numbers?.approvedCodeCount ?? loadingValue} sub="in the code registry" />
      <button
        type="button"
        className={`${TILE_CLASSES} cursor-pointer hover:border-rs-green ${
          hasPending ? "border-rs-amber/45 bg-rs-amber/9" : "border-rs-jet bg-rs-off-black"
        }`}
        onClick={onShowPendingCodes}
      >
        <span className={LABEL_CLASSES}>Pending codes</span>
        <span
          className={`text-[22px] font-semibold leading-tight tabular-nums ${hasPending ? "text-rs-amber" : "text-white"}`}
        >
          {pendingCodeCount}
        </span>
        <span className={SUB_CLASSES}>{hasPending ? "waiting for approval · open" : "nothing to approve"}</span>
      </button>
      <StatTile label="Grades with a TDS" value={gradesWithTdsValue} sub={gradesWithTdsSub} />
    </div>
  );
}
